import React, { useState, useEffect, useCallback } from 'react';
import ProductCard from './ProductCard';
import { productsAPI } from '../services/api';
import { wishlistAPI } from '../services/api';
import { Suspense, useTransition } from 'react';
import Skeleton from './SkeletonProductCard';
import { Loader2 } from 'lucide-react';

const Dashboard = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPending, startTransition] = useTransition();
  const [wishlistData, setWishlistData] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const fetchProducts = useCallback(async (targetPage = 1, isLoadMore = false) => {
    startTransition(async () => {
      try {
        if (!isLoadMore) {
          setLoading(true);
        } else {
          setIsFetchingMore(true);
        }

        const params = { page: targetPage, per_page: 8 };
        if (searchTerm) params.search = searchTerm;
        const response = await productsAPI.getAll(params);
        if (isLoadMore) {
          setProducts(prev => [...prev, ...response.data.products]);
        } else {
          setProducts(response.data.products);
        }
        setPage(targetPage);
        setTotalPages(response.data.pagination.total_pages);
      } catch (err) {
        setError('Failed to fetch products');
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    });
  }, [searchTerm]);

  const fetchWishlist = useCallback(async () => {
    const res = await wishlistAPI.getAll();
    const obj = {};
    (res.data || []).forEach(item => {
      const pid = item.product?.id || item.product_id;
      if (pid !== undefined) obj[pid] = item.id;
    });
    setWishlistData(obj);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [searchTerm, fetchProducts]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const handleAddWishlist = async (productId) => {
    await wishlistAPI.add(productId);
    await fetchWishlist();
  };
  const handleRemoveWishlist = async (wishlistItemId) => {
    await wishlistAPI.remove(wishlistItemId);
    await fetchWishlist();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content */}
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8">

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-8 mt-6">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Products Grid */}
        <Suspense fallback={<div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">{Array.from({length: 8}).map((_,i) => <Skeleton key={i} />)}</div>}>
          {loading || (isPending && products.length === 0) ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({length: 8}).map((_, i) => <Skeleton key={i} />)}
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-700">{error}</p>
              <button
                onClick={() => fetchProducts(1, false)}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
              <p className="text-gray-600 text-lg">No products found</p>
              <p className="text-gray-500">
                Try adjusting your search criteria
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isWishlisted={Object.hasOwn(wishlistData, product.id)}
                    wishlistItemId={wishlistData[product.id]}
                    onAddWishlist={() => handleAddWishlist(product.id)}
                    onRemoveWishlist={() => handleRemoveWishlist(wishlistData[product.id])}
                  />
                ))}
              </div>
              {page < totalPages && (
                <div className="mt-12 mb-16 text-center">
                  <button
                    onClick={() => fetchProducts(page + 1, true)}
                    disabled={isFetchingMore}
                    className="inline-flex items-center px-8 py-3 bg-green-600 text-white rounded-full font-medium hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                  >
                    {isFetchingMore ? (
                      <>
                        <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                        Loading...
                      </>
                    ) : (
                      'Load More Products'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </Suspense>
      </main>
    </div>
  );
};

export default Dashboard;
